import { AsyncPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
  MatNoDataRow,
} from '@angular/material/table';
import { RootState } from '@next/core-lib/app';
import {
  DaterangeConfig,
  TotalsLegacyWarningDialogButtonComponent,
  IdentifierTriggerComponent,
  NotificationEmptyComponent,
} from '@next/core-lib/components';

import { ExportFormat, ExportComponent } from '@next/core-lib/export';
import { LocaleService, TranslatePipe, AgrDatePipe, AgrNumberPipe } from '@next/core-lib/i18n';
import { FormatLocationPipe } from '@next/core-lib/pipes';
import { Settings, SettingsService, SettingsStorage } from '@next/core-lib/settings';
import { TableColumnTypes } from '@next/core-lib/table';
import {
  ExportOptionsV2,
  TableComponent,
  TableSearchTemplateDirective,
  TableSearchComponent,
  TableFiltersTemplateDirective,
  TableDateRangeFilterComponent,
  TableSelectFilterComponent,
  TableTopBarTemplateDirective,
  TableTopBarComponent,
  TableSelectedItemsComponent,
  TableExportDirective,
} from '@next/core-lib/table-v2';
import { UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { combineLatest, of, Subscription } from 'rxjs';
import { distinctUntilChanged, map, skipWhile, take } from 'rxjs/operators';
import {
  DeliveryColumnTypes,
  DeliveryLineWithDelivery,
} from 'src/app/delivery-data/delivery-data.model';
import { DeliveryDataService } from 'src/app/delivery-data/services/delivery-data.service';
import { printDeliveryReportRequest } from 'src/app/delivery-data/store/delivery-data.actions';
import { settingsContextName, SettingsKeys } from 'src/app/shared/models/shared.models';
import { FormatBrsNumberPipe } from 'src/app/shared/pipes/format-brs-number.pipe';
import { DateService } from 'src/app/shared/services/date.service';
import { selectCustomerBrsNumbers } from 'src/app/store/selectors';

import { BexOverviewDataFilters } from '../../../bex/components/models/overview.model';
import { DateRange } from '../../../shared/models/date-range.model';
import { MineralOverviewDataSource } from '../../datasource/mineral-overview.datasource';
import {
  displayColumns,
  MineralOverviewDataFilters,
  MineralOverviewViewModel,
} from '../../models/mineral.model';

@Component({
  selector: 'app-mineral-overview',
  templateUrl: './mineral-overview.component.html',
  styleUrls: ['./mineral-overview.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [FormatBrsNumberPipe, FormatLocationPipe],
  imports: [
    TableComponent,
    MatSort,
    TableSearchTemplateDirective,
    ReactiveFormsModule,
    TableSearchComponent,
    TableFiltersTemplateDirective,
    TableDateRangeFilterComponent,
    TableSelectFilterComponent,
    TableTopBarTemplateDirective,
    TableTopBarComponent,
    TableSelectedItemsComponent,
    MatSlideToggle,
    TotalsLegacyWarningDialogButtonComponent,
    ExportComponent,
    TableExportDirective,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    IdentifierTriggerComponent,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatNoDataRow,
    NotificationEmptyComponent,
    AsyncPipe,
    TranslatePipe,
    AgrDatePipe,
    AgrNumberPipe,
  ],
})
export class MineralOverviewComponent implements OnInit, OnDestroy {
  readonly dataSource = new MineralOverviewDataSource(this._deliveryDataService);
  readonly displayColumns = displayColumns;
  readonly subscriptions: Subscription[] = [];

  readonly searchAndFilters = new FormGroup({
    search: new FormControl(),
    brsNumber: new FormControl(),
    dateRange: new FormControl(),
  });

  readonly showTotalsToggle = new FormControl();

  readonly exportOptions: ExportOptionsV2;

  readonly brsNumbers$ = this.store.select(selectCustomerBrsNumbers).pipe(
    skipWhile((data) => !data?.length),
    distinctUntilChanged(
      (a, b) => a.length === b.length && a.every((p, index) => p.brsNumber === b[index].brsNumber),
    ),
  );

  readonly dateRangeConfig = this.dateService.createDateConfigWithCalendarYearOptions();

  public brsOptions$ = this.brsNumbers$.pipe(
    map((brsNumbers) =>
      brsNumbers.map((customerBrsNumber) => ({
        value: customerBrsNumber,
        viewValue: this.formatBrsNumberPipe.transform(customerBrsNumber),
      })),
    ),
  );

  constructor(
    private readonly _deliveryDataService: DeliveryDataService,
    private readonly localeService: LocaleService,
    private readonly settingsService: SettingsService,
    private readonly store: Store<RootState>,
    private readonly formatBrsNumberPipe: FormatBrsNumberPipe,
    private readonly dateService: DateService,
  ) {
    this.exportOptions = {
      formats: [
        {
          format: ExportFormat.xlsx,
          name: 'XLSX',
        },
        {
          format: ExportFormat.csv,
          name: 'CSV',
        },
        {
          name: this.localeService.translate('common.printReport'),
          export: (data: MineralOverviewViewModel[]) => this.printReport(data),
        },
      ],
      fileName: this.localeService.translate('mineral.mineral-overview.fileName'),
      columns: displayColumns.map((col) => ({
        id: col,
        name: this.localeService.translate(`mineral.mineral-overview.${col}`),
      })),
    };

    this.subscriptions.push(
      this.settingsService
        .getSettings({
          contextName: settingsContextName,
          storage: SettingsStorage.PlatformSession,
          key: SettingsKeys.dateRange,
        })
        .pipe(take(1))
        .subscribe((settings) => {
          const dateRange = this.getDateRange(settings);
          const filters = this.searchAndFilters.value as BexOverviewDataFilters;

          filters.dateRange = dateRange;
          this.dateRangeConfig.config.initial = dateRange;
        }),
    );
  }

  ngOnInit(): void {
    this.searchAndFilters.valueChanges.subscribe(() => {
      const searchFilters = this.searchAndFilters.value as BexOverviewDataFilters;
      const { dateRange } = searchFilters;
      this.dataSource.applyFilters(searchFilters);

      this.settingsService
        .setSettings({
          contextName: settingsContextName,
          storage: SettingsStorage.PlatformSession,
          key: SettingsKeys.dateRange,
          data: {
            startDate: dateRange.startDate.toISOString(),
            endDate: dateRange.endDate.toISOString(),
            description: dateRange.description,
          },
        })
        .subscribe();
    });

    this.subscriptions.push(
      this.showTotalsToggle.valueChanges.subscribe((showTotals) =>
        this.settingsService
          .setSettings({
            contextName: settingsContextName,
            storage: SettingsStorage.PlatformSession,
            key: SettingsKeys.totals,
            data: showTotals,
          })
          .pipe(take(1))
          .subscribe(),
      ),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  isEqual(a: MineralOverviewViewModel, b: MineralOverviewViewModel) {
    return a.id === b.id && a.deliveryId === b.deliveryId;
  }

  resetFilters() {
    this.searchAndFilters.reset({
      search: null,
      brsNumber: null,
      dateRange: new DaterangeConfig().config.initial,
    });
  }

  getTableColumnType(dCol: DeliveryColumnTypes) {
    return dCol === 'deliveryDate'
      ? TableColumnTypes.DATE
      : ['unitQuantity', 'phosphateTotal', 'nitrogenTotal'].includes(dCol)
        ? TableColumnTypes.NUMBER
        : TableColumnTypes.STRING;
  }

  private printReport = (data: MineralOverviewViewModel[]) =>
    combineLatest([
      of(this.searchAndFilters.value as MineralOverviewDataFilters),
      this.store.select(UserSelectors.selectCustomer),
      this.dataSource.order$,
    ])
      .pipe(take(1))
      .subscribe(([filters, customer, order]) => {
        const startDate = filters.dateRange.startDate;
        const endDate = filters.dateRange.endDate;
        data.sort((objectA, objectB) => {
          const propertyA = objectA[order.column as keyof MineralOverviewViewModel];
          const propertyB = objectB[order.column as keyof MineralOverviewViewModel];
          const result = propertyA > propertyB ? 1 : propertyA < propertyB ? -1 : 0;
          return order.descending ? -result : result;
        });
        this.store.dispatch(
          printDeliveryReportRequest({
            report: {
              title: this.localeService.translate('mineral.mineral-overview.pfdExportTitle'),
              subTitle: `${this.localeService.translate(
                'mineral.report.sub-title',
              )} ${this._deliveryDataService.formatDateTime(new Date())}`,
              fileName: this._deliveryDataService.formatExportFileName(
                this.localeService.translate('mineral.mineral-overview.fileName'),
                customer.externalId,
                startDate,
                endDate,
              ),
              legend: {
                [this.localeService.translate('mineral.report.legend.customer')]:
                  `${customer.name} (${customer.externalId})`,
                [this.localeService.translate('mineral.report.legend.brs-number')]:
                  this.formatBrsNumberPipe.transform(filters.brsNumber),
                [this.localeService.translate('mineral.report.legend.start-date')]:
                  this._deliveryDataService.formatDateTime(startDate),
                [this.localeService.translate('mineral.report.legend.end-date')]:
                  this._deliveryDataService.formatDateTime(endDate),
              },
              tableHeadings:
                this.exportOptions.columns.map((col: { name: string }) => col.name) ?? [],
              tableFooter: [
                this.localeService.translate('mineral.report.disclaimer1'),
                this.localeService.translate('mineral.report.disclaimer2'),
              ],

              contactInformation: {
                email: this.localeService.translate('mineral.report.contact.email'),
                phoneNumber: this.localeService.translate('mineral.report.contact.phone-number'),
              },
              rows: this._deliveryDataService.mapToPdfRows(
                this.displayColumns.map((dCol) => ({
                  id: dCol,
                  type: this.getTableColumnType(dCol),
                  digitsInfo: '1.0-2',
                })),
                data as unknown as DeliveryLineWithDelivery[],
              ),
            },
          }),
        );
      });

  private getDateRange(settings: Settings[]): DateRange {
    const selectedDateRange = settings?.find((setting) =>
      (setting.settingsId + '').includes(SettingsKeys.dateRange),
    )?.data as { [key: string]: string };

    const startDate = selectedDateRange?.startDate
      ? new Date(selectedDateRange.startDate)
      : this.dateService.defaultRecentStartDate();

    const endDate = selectedDateRange?.endDate
      ? new Date(selectedDateRange.endDate)
      : this.dateService.defaultRecentEndDate();

    return {
      startDate,
      endDate,
      description: selectedDateRange?.description,
    };
  }
}
